import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import RobotsTag from "./components/seo/RobotsTag";

// Layout
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Pages vitrine
import HomePage from "./components/pages/HomePage";
import ServicePage from "./components/pages/ServicePage";
import CityPage from "./components/pages/CityPage";
import AboutPage from "./components/pages/AboutPage";
import ContactPage from "./components/pages/ContactPage";
import MentionsLegalesPage from "./components/pages/MentionsLegalesPage";
import PolitiqueConfidentialitePage from "./components/pages/PolitiqueConfidentialitePage";

// SEO Local Pages - Vitrier Villiers-sur-Marne
import VitrerieLocalPage from "./components/pages/VitrerieLocalPage";
import VitrerieUrgencePage from "./components/pages/VitrerieUrgencePage";
import BrisDeGlacePage from "./components/pages/BrisDeGlacePage";
import VitreCasseePage from "./components/pages/VitreCasseePage";
import RenovationFenetresPage from "./components/pages/RenovationFenetresPage";
import PartenariatAgencesPage from "./components/pages/PartenariatAgencesPage";
import RenovationVillePage from "./components/pages/RenovationVillePage";

// GMAO (espace privé)
import LoginPage from "./components/pages/LoginPage";
import PublicQuotePage from "./components/pages/PublicQuotePage";
import PublicPaymentPage from "./components/pages/PublicPaymentPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import InstallPrompt from "./components/pwa/InstallPrompt";
import AdminDashboard from "./components/gmao/AdminDashboard";
import AgencyDashboard from "./components/gmao/AgencyDashboard";
import ClientDashboard from "./components/gmao/ClientDashboard";
import TechnicianDashboard from "./components/gmao/TechnicianDashboard";

// Vitrine layout (Header + Footer publics)
const VitrineLayout = () => (
  <>
    <RobotsTag content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
    <Header />
    <main>
      <Outlet />
    </main>
    <Footer />
  </>
);

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* ===== Site vitrine (public, SEO) ===== */}
              <Route element={<VitrineLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/services/:serviceId" element={<ServicePage />} />
                <Route path="/villes/:cityId" element={<CityPage />} />
                <Route path="/a-propos" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
                <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />

                {/* SEO Local Pages */}
                <Route path="/vitrier-villiers-sur-marne" element={<VitrerieLocalPage />} />
                <Route path="/vitrier-urgence-villiers-sur-marne" element={<VitrerieUrgencePage />} />
                <Route path="/bris-de-glace-villiers-sur-marne" element={<BrisDeGlacePage />} />
                <Route path="/remplacement-vitre-cassee-villiers-sur-marne" element={<VitreCasseePage />} />
                <Route path="/renovation-fenetres-paiement-plusieurs-fois-villiers-sur-marne" element={<RenovationFenetresPage />} />

                {/* Pages Professionnels */}
                <Route path="/partenariat-agences-immobilieres" element={<PartenariatAgencesPage />} />

                {/* Pages SEO Rénovation - Toutes les villes */}
                <Route path="/renovation-fenetres-noisy-le-grand" element={<RenovationVillePage citySlug="noisy-le-grand" />} />
                <Route path="/renovation-fenetres-bry-sur-marne" element={<RenovationVillePage citySlug="bry-sur-marne" />} />
                <Route path="/renovation-fenetres-le-perreux-sur-marne" element={<RenovationVillePage citySlug="le-perreux-sur-marne" />} />
                <Route path="/renovation-fenetres-chennevieres-sur-marne" element={<RenovationVillePage citySlug="chennevieres-sur-marne" />} />
                <Route path="/renovation-fenetres-le-plessis-trevise" element={<RenovationVillePage citySlug="le-plessis-trevise" />} />
                <Route path="/renovation-fenetres-saint-maur-des-fosses" element={<RenovationVillePage citySlug="saint-maur-des-fosses" />} />
                <Route path="/renovation-fenetres-neuilly-sur-marne" element={<RenovationVillePage citySlug="neuilly-sur-marne" />} />
                <Route path="/renovation-fenetres-neuilly-plaisance" element={<RenovationVillePage citySlug="neuilly-plaisance" />} />
                <Route path="/renovation-fenetres-gournay-sur-marne" element={<RenovationVillePage citySlug="gournay-sur-marne" />} />
                <Route path="/renovation-fenetres-torcy" element={<RenovationVillePage citySlug="torcy" />} />
                <Route path="/renovation-fenetres-champigny-sur-marne" element={<RenovationVillePage citySlug="champigny-sur-marne" />} />
                <Route path="/renovation-fenetres-nogent-sur-marne" element={<RenovationVillePage citySlug="nogent-sur-marne" />} />
                <Route path="/renovation-fenetres-creteil" element={<RenovationVillePage citySlug="creteil" />} />
              </Route>

              {/* ===== Espace privé GMAO (noindex) ===== */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/devis/:token" element={<PublicQuotePage />} />
              <Route path="/paiement-groupe/:token" element={<PublicPaymentPage />} />

              <Route element={<ProtectedRoute allow={["admin"]} />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/gmao-admin" element={<AdminDashboard />} />
              </Route>
              <Route element={<ProtectedRoute allow={["agence"]} />}>
                <Route path="/gmao" element={<AgencyDashboard />} />
              </Route>
              <Route element={<ProtectedRoute allow={["client"]} />}>
                <Route path="/client" element={<ClientDashboard />} />
              </Route>
              <Route element={<ProtectedRoute allow={["technicien"]} />}>
                <Route path="/technicien" element={<TechnicianDashboard />} />
              </Route>
            </Routes>
          </AuthProvider>
          <Toaster position="top-right" />
          <InstallPrompt />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
